import React from 'react';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { formatHallCurrency } from '../utils/hallFinancials';

const HALL_ACCENT = '#4ce1b6';

export const HALL_VENDOR_SETTLEMENT_OPTIONS = [
  {
    value: 'due',
    label: 'Due',
    helpCreate: 'No payout now — full contract stays as vendor due.',
    helpEdit: 'No additional payout — remaining contract stays as vendor due.',
  },
  {
    value: 'partial',
    label: 'Partial',
    helpCreate: 'Pay a deposit now; remaining stays due. Saved in vendor payments.',
    helpEdit: 'Pay more against the remaining due. Saved in vendor payments.',
  },
  {
    value: 'full',
    label: 'Full',
    helpCreate: 'Pay the full contract now. Saved in vendor payments.',
    helpEdit: 'Settle the remaining vendor due now. Saved in vendor payments.',
  },
];

export function HallVendorSummaryRow({
  label,
  value,
  isDark,
  emphasize = false,
  valueColor,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1.5,
        py: emphasize ? 0.55 : 0.35,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: isDark ? '#94a3b8' : '#64748b',
          fontWeight: emphasize ? 800 : 600,
          fontSize: emphasize ? '0.78rem' : '0.72rem',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: emphasize ? 800 : 700,
          fontSize: emphasize ? '0.95rem' : '0.84rem',
          color: valueColor || (isDark ? '#e2e8f0' : '#0f172a'),
          flexShrink: 0,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Contract / paid / due as stacked rows (payment drawer). */
export function HallVendorSettlementRows({
  contractAmount,
  paidAmount,
  dueAmount,
  isDark,
  paidLabel = 'Paid to vendor',
  dueLabel = 'Vendor due',
}) {
  return (
    <>
      <HallVendorSummaryRow
        label="Contract"
        value={formatHallCurrency(contractAmount)}
        isDark={isDark}
      />
      <HallVendorSummaryRow
        label={paidLabel}
        value={formatHallCurrency(paidAmount)}
        isDark={isDark}
        valueColor={isDark ? '#86efac' : '#166534'}
      />
      <Divider sx={{ my: 1, borderColor: isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08) }} />
      <HallVendorSummaryRow
        label={dueLabel}
        value={formatHallCurrency(dueAmount)}
        isDark={isDark}
        emphasize
        valueColor={
          dueAmount > 0
            ? (isDark ? '#fdba74' : '#c2410c')
            : (isDark ? '#86efac' : '#166534')
        }
      />
    </>
  );
}

/** Contract / paid / due as three compact tiles (edit assignment). */
export function HallVendorSettlementTiles({
  contractAmount,
  paidAmount,
  dueAmount,
  isDark,
}) {
  const items = [
    { label: 'Total', value: formatHallCurrency(contractAmount) },
    {
      label: 'Paid',
      value: formatHallCurrency(paidAmount),
      color: isDark ? '#bbf7d0' : '#166534',
    },
    {
      label: 'Due',
      value: formatHallCurrency(dueAmount),
      color: dueAmount > 0
        ? (isDark ? '#fde68a' : '#b45309')
        : (isDark ? '#bbf7d0' : '#166534'),
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 1,
      }}
    >
      {items.map((item) => (
        <Box
          key={item.label}
          sx={{
            px: 1,
            py: 0.75,
            borderRadius: 1.5,
            border: `1px solid ${isDark ? alpha('#64748b', 0.3) : alpha('#cbd5e1', 0.65)}`,
            backgroundColor: isDark ? alpha('#0f172a', 0.4) : alpha('#f8fafc', 0.95),
          }}
        >
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
            {item.label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              color: item.color || (isDark ? '#f8fafc' : '#0f172a'),
              fontSize: '0.88rem',
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export function HallVendorSettlementChips({
  value,
  onChange,
  isDark,
  options = HALL_VENDOR_SETTLEMENT_OPTIONS,
  canRecordPayout = true,
  helpText = '',
  title = 'Settlement',
}) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', fontSize: '0.92rem', mb: 0.35 }}>
        {title}
      </Typography>
      {helpText ? (
        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, display: 'block', mb: 1 }}>
          {helpText}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {options.map((option) => {
          const selected = value === option.value;
          const disabled = option.value !== 'due' && !canRecordPayout;
          return (
            <Chip
              key={option.value}
              label={option.label}
              onClick={() => {
                if (disabled) return;
                onChange?.(option.value);
              }}
              sx={{
                fontWeight: 800,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                minWidth: 72,
                color: selected ? '#04120e' : (isDark ? '#cbd5e1' : '#334155'),
                bgcolor: selected
                  ? HALL_ACCENT
                  : (isDark ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1)),
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

/** Partial / Full chips used by the standalone payout drawer. */
export function HallVendorPayoutTypeChips({
  value,
  onChange,
  isDark,
  options = [
    { value: 'partial', label: 'Partial' },
    { value: 'full', label: 'Full' },
  ],
}) {
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <Chip
            key={option.value}
            label={option.label}
            onClick={() => onChange?.(option.value)}
            sx={{
              fontWeight: 800,
              cursor: 'pointer',
              minWidth: 72,
              color: selected ? '#04120e' : (isDark ? '#cbd5e1' : '#334155'),
              bgcolor: selected
                ? HALL_ACCENT
                : (isDark ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1)),
            }}
          />
        );
      })}
    </Stack>
  );
}
